import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import { useInquiry } from '../../contexts/InquiryContext';
import { useAuth } from '../../contexts/AuthContext';
import { useCategoryContext } from '../../contexts/CategoryContext';
import { supabase } from '../../lib/supabase';

const ITEMS_PER_PAGE = 10;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'hwp', 'zip', 'txt'];

const isImageFile = (fileName: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return ['jpg', 'jpeg', 'png', 'gif'].includes(ext);
};

export default function InquiryPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { inquiries, addInquiry, deleteInquiry, updateInquiryReply, loading, refreshInquiries } = useInquiry();
  const { isAdmin } = useAuth();
  const { dynamicProducts } = useCategoryContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    name: '',
    phone: '',
    email: '',
    company: '',
    message: '',
    password: '',
    product_info: ''
  });
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [sourceProductId, setSourceProductId] = useState<string | null>(null);

  const [selectedInquiry, setSelectedInquiry] = useState<any>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replySubmitting, setReplySubmitting] = useState(false);

  // 새로고침 후 문의 리스트로 스크롤
  useEffect(() => {
    const shouldScroll = sessionStorage.getItem('scrollToInquiryList');
    if (shouldScroll === 'true') {
      sessionStorage.removeItem('scrollToInquiryList');
      setTimeout(() => {
        const el = document.getElementById('inquiry-list-section');
        if (el) {
          el.scrollIntoView({ behavior: 'auto' });
        }
      }, 300);
    }
  }, []);

  const filteredInquiries = searchKeyword.trim()
    ? inquiries.filter(
        (inq) =>
          inq.title.toLowerCase().includes(searchKeyword.trim().toLowerCase()) ||
          inq.name.toLowerCase().includes(searchKeyword.trim().toLowerCase())
      )
    : inquiries;

  const totalPages = Math.ceil(filteredInquiries.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentInquiries = filteredInquiries.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  const handleInquiryClick = (inquiry: any) => {
    // 담당자는 비밀번호 없이 바로 열람
    if (isAdmin) {
      setSelectedInquiry(inquiry);
      setReplyText(inquiry.reply_content || '');
      setShowReplyForm(false);
      setShowContentModal(true);
    } else {
      setSelectedInquiry(inquiry);
      setShowPasswordModal(true);
      setPasswordInput('');
      setPasswordError(false);
    }
  };

  const handlePasswordSubmit = () => {
    if (selectedInquiry && passwordInput === selectedInquiry.password) {
      setShowPasswordModal(false);
      setReplyText(selectedInquiry.reply_content || '');
      setShowReplyForm(false);
      setShowContentModal(true);
      setPasswordError(false);
    } else {
      setPasswordError(true);
    }
  };

  const closeModals = () => {
    setShowPasswordModal(false);
    setShowContentModal(false);
    setShowDeleteConfirm(false);
    setSelectedInquiry(null);
    setPasswordInput('');
    setPasswordError(false);
    setReplyText('');
    setShowReplyForm(false);
  };

  const handleDelete = async () => {
    if (selectedInquiry) {
      try {
        await deleteInquiry(selectedInquiry.id);
        closeModals();
        alert('문의가 삭제되었습니다.');
        if (sourceProductId) {
          sessionStorage.setItem('scrollToProductInquiry', 'true');
          window.location.href = `${__BASE_PATH__}/products/${sourceProductId}`;
        } else {
          sessionStorage.setItem('scrollToInquiryList', 'true');
          window.location.reload();
        }
      } catch (error) {
        alert('삭제 중 오류가 발생했습니다.');
      }
    }
  };

  const handleReplySubmit = async () => {
    if (!selectedInquiry || !replyText.trim()) return;
    try {
      setReplySubmitting(true);
      await updateInquiryReply(selectedInquiry.id, replyText.trim());
      setSelectedInquiry({ ...selectedInquiry, reply_content: replyText.trim(), reply_date: new Date().toISOString(), status: '답변완료' });
      setShowReplyForm(false);
    } catch (error) {
      console.error('답변 등록 오류:', error);
      alert('답변 등록 중 오류가 발생했습니다.');
    } finally {
      setReplySubmitting(false);
    }
  };

  // 제품 상세페이지에서 전달된 제품 정보 처리
  useEffect(() => {
    const productState = location.state as {
      productId?: string;
      productNameEn?: string;
      productName?: string;
      productVolume?: string;
    } | null;

    if (productState?.productId) {
      const productInfoText = `${productState.productNameEn} / ${productState.productName} / ${productState.productVolume}`;
      setFormData(prev => ({
        ...prev,
        product_info: productInfoText
      }));
      setShowForm(true);
      setSourceProductId(productState.productId);
      
      // state 초기화 (뒤로가기 시 중복 적용 방지)
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleFileSelect = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      alert('지원하지 않는 파일 형식입니다.\n허용 형식: ' + ALLOWED_EXTENSIONS.join(', '));
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      alert('파일 크기는 10MB 이하만 가능합니다.');
      return;
    }
    setAttachmentFile(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const removeFile = () => {
    setAttachmentFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'ri-image-line';
    if (ext === 'pdf') return 'ri-file-pdf-2-line';
    if (['doc', 'docx'].includes(ext)) return 'ri-file-word-line';
    if (['xls', 'xlsx'].includes(ext)) return 'ri-file-excel-line';
    if (['ppt', 'pptx'].includes(ext)) return 'ri-file-ppt-line';
    if (ext === 'zip') return 'ri-file-zip-line';
    return 'ri-file-line';
  };

  const uploadFile = async (file: File): Promise<{ url: string; name: string }> => {
    const timestamp = Date.now();
    const ext = file.name.split('.').pop();
    const filePath = `${timestamp}_${Math.random().toString(36).substring(2, 8)}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('inquiry-attachments')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from('inquiry-attachments')
      .getPublicUrl(filePath);

    return { url: data.publicUrl, name: file.name };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title || !formData.name || !formData.phone || !formData.email || !formData.message || !formData.password) {
      alert('모든 필수 필드를 입력해주세요.');
      return;
    }

    if (formData.message.length > 500) {
      alert('문의 내용은 500자를 초과할 수 없습니다.');
      return;
    }

    try {
      setUploading(true);
      let attachmentUrl: string | null = null;
      let attachmentName: string | null = null;

      if (attachmentFile) {
        const result = await uploadFile(attachmentFile);
        attachmentUrl = result.url;
        attachmentName = result.name;
      }

      await addInquiry({
        title: formData.title,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        product_info: formData.product_info,
        message: formData.message,
        password: formData.password,
        attachment_url: attachmentUrl,
        attachment_name: attachmentName
      });

      alert('문의가 성공적으로 등록되었습니다.');
      if (sourceProductId) {
        sessionStorage.setItem('scrollToProductInquiry', 'true');
        window.location.href = `${__BASE_PATH__}/products/${sourceProductId}`;
      } else {
        sessionStorage.setItem('scrollToInquiryList', 'true');
        window.location.reload();
      }
    } catch (error) {
      console.error('문의 등록 오류:', error);
      alert('문의 등록 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-0 md:pt-20">
        <section className="bg-white">
          <div className="max-w-[1400px] mx-auto px-5 py-5 md:py-8 text-center">
            <h1 className="text-2xl md:text-4xl font-bold text-[#222222] mb-2 md:mb-3">고객 문의</h1>
            <p className="text-sm md:text-base text-[#222222]">제품 및 서비스에 대한 문의사항을 남겨주세요</p>
          </div>
        </section>

        <section className="py-8 md:py-16 pb-20 md:pb-40 bg-white">
          <div className="max-w-[1400px] mx-auto px-5" id="inquiry-list-section">
            {/* 문의 작성 버튼 */}
            {!showForm && (
              <div className="flex justify-end mb-4 md:mb-6">
                <button
                  onClick={() => setShowForm(true)}
                  className="bg-primary text-white px-5 md:px-6 py-2 md:py-2.5 rounded-full text-sm md:text-base font-medium hover:bg-primary-dark transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-edit-line text-base md:text-lg"></i>
                  문의 작성
                </button>
              </div>
            )}

            {/* 문의 양식 */}
            {showForm && (
              <div className="bg-[#F8FAF5] rounded-lg p-5 md:p-10 mb-6 md:mb-8">
                <h3 className="text-base md:text-lg font-bold text-[#222222] mb-4 md:mb-6">문의하기</h3>
                <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
                  <input
                    type="text"
                    name="title"
                    placeholder="제목 *"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full bg-white rounded-lg px-4 py-3 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                    <input
                      type="text"
                      name="name"
                      placeholder="이름 *"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-white rounded-lg px-4 py-3 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    />
                    <input
                      type="tel"
                      name="phone"
                      placeholder="연락처 *"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full bg-white rounded-lg px-4 py-3 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                    <input
                      type="email"
                      name="email"
                      placeholder="이메일 *"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-white rounded-lg px-4 py-3 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    />
                    <input
                      type="password"
                      name="password"
                      placeholder="비밀번호 (게시글 확인용) *"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full bg-white rounded-lg px-4 py-3 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    />
                  </div>
                  <input
                    type="text"
                    name="company"
                    placeholder="회사명 (선택사항)"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full bg-white rounded-lg px-4 py-3 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <div className="relative">
                    <select
                      name="product_info"
                      value={formData.product_info}
                      onChange={(e) => setFormData({ ...formData, product_info: e.target.value })}
                      className="w-full bg-white rounded-lg px-4 py-3 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-primary appearance-none cursor-pointer"
                    >
                      <option value="">문의 제품 선택 (선택사항)</option>
                      {dynamicProducts.map((p) => (
                        <option key={p.id} value={`${p.nameEn} / ${p.name} / ${p.volumeOptions && p.volumeOptions.length > 1 ? p.volumeOptions.join(', ') : p.volume}`}>
                          {p.name} ({p.nameEn}) - {p.volumeOptions && p.volumeOptions.length > 1 ? p.volumeOptions.join(', ') : p.volume}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                      <i className="ri-arrow-down-s-line text-gray-400 text-lg"></i>
                    </div>
                  </div>
                  <div className="relative">
                    <textarea
                      name="message"
                      placeholder="문의 내용을 입력해주세요 (최대 500자) *"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      maxLength={500}
                      className="w-full bg-white rounded-lg px-4 py-3 text-sm md:text-base h-32 md:h-40 resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    />
                    <div className="absolute bottom-3 md:bottom-4 right-3 md:right-4 text-xs md:text-sm text-gray-500">
                      {formData.message.length}/500
                    </div>
                  </div>

                  {/* 첨부파일 업로드 */}
                  <div>
                    <label className="block text-sm font-medium text-[#222222] mb-2">첨부파일 (선택사항)</label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileChange}
                      className="hidden"
                      accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.hwp,.zip,.txt"
                    />
                    {!attachmentFile ? (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        onDrop={handleDrop}
                        onDragOver={(e) => e.preventDefault()}
                        className="w-full bg-white border-2 border-dashed border-gray-300 rounded-lg px-4 py-6 md:py-8 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                      >
                        <i className="ri-upload-cloud-2-line text-2xl md:text-3xl text-gray-400 mb-2 block"></i>
                        <p className="text-sm text-gray-500 mb-1">클릭하거나 파일을 드래그하여 업로드</p>
                        <p className="text-xs text-gray-400">최대 10MB / jpg, png, pdf, doc, xls, ppt, hwp, zip, txt</p>
                      </div>
                    ) : (
                      <div className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 flex items-center justify-center shrink-0">
                            <i className={`${getFileIcon(attachmentFile.name)} text-xl text-primary`}></i>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm text-[#222222] font-medium truncate">{attachmentFile.name}</p>
                            <p className="text-xs text-gray-400">{formatFileSize(attachmentFile.size)}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={removeFile}
                          className="w-7 h-7 flex items-center justify-center shrink-0 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                        >
                          <i className="ri-close-circle-line text-lg"></i>
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-3 md:gap-4">
                    <button
                      type="submit"
                      disabled={uploading}
                      className="bg-primary-dark text-white px-5 md:px-6 py-2 md:py-2.5 rounded-full text-sm md:text-base font-medium hover:bg-primary transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {uploading && <i className="ri-loader-4-line animate-spin text-base"></i>}
                      {uploading ? '등록 중...' : '문의 등록'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowForm(false); removeFile(); }}
                      className="bg-gray-400 text-white px-5 md:px-6 py-2 md:py-2.5 rounded-full text-sm md:text-base font-medium hover:bg-gray-500 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      닫기
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* 문의 리스트 */}
            {loading ? (
              <div className="text-center py-20">
                <p className="text-gray-500">로딩 중...</p>
              </div>
            ) : (
              <>
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                  {/* PC 테이블 헤더 */}
                  <div className="hidden md:block bg-gray-100 px-6 py-3">
                    <div className="grid grid-cols-12 gap-4 text-[#222222] font-semibold text-sm">
                      <div className="col-span-1 text-center">번호</div>
                      <div className="col-span-5">제목</div>
                      <div className="col-span-2 text-center">작성자</div>
                      <div className="col-span-2 text-center">작성일</div>
                      <div className="col-span-2 text-center">상태</div>
                    </div>
                  </div>
                  {/* 모바일 테이블 헤더 */}
                  <div className="block md:hidden bg-gray-100 px-4 py-3">
                    <div className="grid grid-cols-12 gap-2 text-[#222222] font-semibold text-xs">
                      <div className="col-span-1 text-center">No</div>
                      <div className="col-span-5">제목</div>
                      <div className="col-span-2 text-center">작성자</div>
                      <div className="col-span-2 text-center">작성일</div>
                      <div className="col-span-2 text-center">상태</div>
                    </div>
                  </div>
                  <div>
                    {currentInquiries.length === 0 ? (
                      <div className="px-4 md:px-6 py-12 md:py-16 text-center">
                        <i className="ri-file-list-3-line text-3xl md:text-4xl text-gray-300 mb-3 block"></i>
                        <p className="text-sm md:text-base text-gray-400">
                          {searchKeyword.trim() ? '검색 결과가 없습니다.' : '등록된 게시글이 없습니다.'}
                        </p>
                      </div>
                    ) : (
                      currentInquiries.map((inquiry, index) => (
                        <div
                          key={inquiry.id}
                          onClick={() => handleInquiryClick(inquiry)}
                          className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-100 hover:bg-primary-light/30 transition-colors cursor-pointer"
                        >
                          {/* PC 레이아웃 */}
                          <div className="hidden md:grid grid-cols-12 gap-4 items-center">
                            <div className="col-span-1 text-center text-[#222222] text-sm">{startIndex + index + 1}</div>
                            <div className="col-span-5 text-[#222222] font-medium text-sm flex items-center gap-2">
                              <i className="ri-lock-line text-sm text-gray-400"></i>
                              {inquiry.title}
                              {inquiry.attachment_url && (
                                <i className="ri-attachment-2 text-sm text-gray-400" title="첨부파일"></i>
                              )}
                            </div>
                            <div className="col-span-2 text-center text-[#222222] text-sm">{inquiry.name}</div>
                            <div className="col-span-2 text-center text-[#222222] text-sm">
                              {new Date(inquiry.created_at).toLocaleDateString('ko-KR')}
                            </div>
                            <div className="col-span-2 text-center">
                              <span className={`text-sm font-medium ${inquiry.status === '답변완료' ? 'text-primary' : 'text-[#222222]'}`}>
                                {inquiry.status === '답변완료' ? '답변완료' : '답변대기'}
                              </span>
                            </div>
                          </div>
                          {/* 모바일 레이아웃 - 게시판 형태 */}
                          <div className="grid md:hidden grid-cols-12 gap-2 items-center">
                            <div className="col-span-1 text-center text-[#222222] text-xs">{startIndex + index + 1}</div>
                            <div className="col-span-5 text-[#222222] font-medium text-xs flex items-center gap-1 truncate">
                              <i className="ri-lock-line text-xs text-gray-400 shrink-0"></i>
                              <span className="truncate">{inquiry.title}</span>
                              {inquiry.attachment_url && (
                                <i className="ri-attachment-2 text-xs text-gray-400 shrink-0"></i>
                              )}
                            </div>
                            <div className="col-span-2 text-center text-[#222222] text-xs truncate">{inquiry.name}</div>
                            <div className="col-span-2 text-center text-[#222222] text-xs">
                              {new Date(inquiry.created_at).toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' })}
                            </div>
                            <div className="col-span-2 text-center">
                              <span className={`text-xs font-medium ${inquiry.status === '답변완료' ? 'text-primary' : 'text-[#222222]'}`}>
                                {inquiry.status === '답변완료' ? '완료' : '대기'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* 페이지네이션 */}
                <div className="flex justify-center items-center mt-2.5 md:mt-4">
                  {totalPages > 1 ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded text-sm transition-colors cursor-pointer ${
                          currentPage === 1
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <i className="ri-skip-back-mini-line text-base md:text-lg"></i>
                      </button>
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded text-sm transition-colors cursor-pointer ${
                          currentPage === 1
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <i className="ri-arrow-left-s-line text-base md:text-lg"></i>
                      </button>

                      {getPageNumbers().map((page) => (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded text-xs md:text-sm font-medium transition-colors cursor-pointer ${
                            currentPage === page
                              ? 'bg-primary text-white'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {page}
                        </button>
                      ))}

                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded text-sm transition-colors cursor-pointer ${
                          currentPage === totalPages
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <i className="ri-arrow-right-s-line text-base md:text-lg"></i>
                      </button>
                      <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded text-sm transition-colors cursor-pointer ${
                          currentPage === totalPages
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <i className="ri-skip-forward-mini-line text-base md:text-lg"></i>
                      </button>
                    </div>
                  ) : (
                    <div></div>
                  )}
                </div>

                {/* 검색 영역 - 게시판 하단 */}
                <div className="flex justify-center mt-2.5 md:mt-4">
                  <div className="w-full md:w-1/2 flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <input
                      type="text"
                      placeholder="제목 또는 작성자 검색"
                      value={searchKeyword}
                      onChange={(e) => {
                        setSearchKeyword(e.target.value);
                        if (e.target.value === '') setCurrentPage(1);
                      }}
                      onKeyDown={handleSearchKeyDown}
                      className="flex-1 min-w-0 bg-white px-3 md:px-4 py-2.5 text-sm focus:outline-none text-[#222222] placeholder-gray-400"
                    />
                    <button
                      onClick={handleSearch}
                      className="px-3 md:px-4 py-2.5 text-gray-500 hover:text-primary transition-colors cursor-pointer shrink-0"
                    >
                      <i className="ri-search-line text-lg"></i>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTopButton />

      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4" onClick={closeModals}>
          <div className="bg-white rounded-lg p-6 md:p-8 w-full max-w-[400px] shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-[#222222]">비밀번호 확인</h3>
              <button onClick={closeModals} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <p className="text-base text-[#222222] mb-5">게시글을 확인하려면 비밀번호를 입력해주세요.</p>
            <input
              type="password"
              placeholder="비밀번호"
              value={passwordInput}
              onChange={(e) => {
                setPasswordInput(e.target.value);
                setPasswordError(false);
              }}
              onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
              className={`w-full bg-gray-50 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-primary mb-2 ${
                passwordError ? 'ring-2 ring-red-400' : ''
              }`}
            />
            {passwordError && (
              <p className="text-sm text-red-500 mb-4">비밀번호가 일치하지 않습니다.</p>
            )}
            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={closeModals}
                className="px-5 py-2.5 text-base text-gray-600 hover:text-gray-800 cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={handlePasswordSubmit}
                className="bg-primary text-white px-5 py-2.5 rounded-lg text-base font-medium hover:bg-primary-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {showContentModal && selectedInquiry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4" onClick={closeModals}>
          <div className="bg-white rounded-lg p-6 md:p-8 w-full max-w-[540px] max-h-[80vh] overflow-y-auto shadow-xl inquiry-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-[#222222]">{selectedInquiry.title || '문의 내용'}</h3>
              <button onClick={closeModals} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            
            {/* 제품 정보 표시 */}
            {selectedInquiry.product_info && (
              <div className="bg-primary-light rounded-lg p-4 mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-product-hunt-line text-primary text-lg"></i>
                  <span className="text-sm font-semibold text-primary-dark">문의 제품</span>
                </div>
                <p className="text-base text-gray-800 font-medium">{selectedInquiry.product_info}</p>
              </div>
            )}

            <div className="border-b border-gray-200 pb-5 mb-5">
              <div className="flex items-center gap-5 text-sm text-gray-500 mb-3">
                <span>작성자: {selectedInquiry.name}</span>
                <span>작성일: {new Date(selectedInquiry.created_at).toLocaleDateString('ko-KR')}</span>
              </div>
              {selectedInquiry.email && (
                <p className="text-sm text-gray-600 mb-1">이메일: {selectedInquiry.email}</p>
              )}
              {selectedInquiry.phone && (
                <p className="text-sm text-gray-600 mb-1">연락처: {selectedInquiry.phone}</p>
              )}
              {selectedInquiry.company && (
                <p className="text-sm text-gray-600">회사명: {selectedInquiry.company}</p>
              )}
            </div>
            <div className="bg-gray-50 rounded-lg p-5 mb-5">
              <p className="text-base text-[#222222] leading-relaxed whitespace-pre-wrap">
                {selectedInquiry.message}
              </p>
            </div>

            {/* 첨부파일 표시 */}
            {selectedInquiry.attachment_url && selectedInquiry.attachment_name && (
              <div className="border border-gray-200 rounded-lg p-4 mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-attachment-2 text-gray-500 text-base"></i>
                  <span className="text-sm font-semibold text-[#222222]">첨부파일</span>
                </div>
                {isImageFile(selectedInquiry.attachment_name) && (
                  <div
                    className="mb-3 rounded-lg overflow-hidden bg-gray-50 cursor-pointer"
                    onClick={() => setShowImagePreview(true)}
                  >
                    <img
                      src={selectedInquiry.attachment_url}
                      alt={selectedInquiry.attachment_name}
                      className="w-full max-h-[300px] object-contain"
                    />
                  </div>
                )}
                <a
                  href={selectedInquiry.attachment_url}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center shrink-0">
                    <i className={`${getFileIcon(selectedInquiry.attachment_name)} text-xl text-primary`}></i>
                  </div>
                  <span className="text-sm text-primary font-medium truncate">{selectedInquiry.attachment_name}</span>
                  <i className="ri-download-2-line text-base text-gray-400 shrink-0 ml-auto"></i>
                </a>
              </div>
            )}

            {/* 답변 영역 */}
            <div className="border-t border-gray-200 pt-5 mb-5">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-chat-3-line text-primary text-lg"></i>
                <span className="text-sm font-bold text-[#222222]">담당자 답변</span>
                {selectedInquiry.status === '답변완료' && (
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">답변완료</span>
                )}
              </div>

              {selectedInquiry.reply_content && !showReplyForm ? (
                <div className="bg-primary-light/50 rounded-lg p-5 mb-3">
                  <p className="text-base text-[#222222] leading-relaxed whitespace-pre-wrap">{selectedInquiry.reply_content}</p>
                  {selectedInquiry.reply_date && (
                    <p className="text-xs text-gray-400 mt-3">
                      답변일: {new Date(selectedInquiry.reply_date).toLocaleDateString('ko-KR')} {new Date(selectedInquiry.reply_date).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => { setShowReplyForm(true); setReplyText(selectedInquiry.reply_content || ''); }}
                      className="mt-3 text-sm text-primary hover:text-primary-dark font-medium cursor-pointer flex items-center gap-1"
                    >
                      <i className="ri-edit-2-line text-sm"></i>
                      답변 수정
                    </button>
                  )}
                </div>
              ) : !showReplyForm && !isAdmin ? (
                <div className="bg-gray-50 rounded-lg p-5 text-center">
                  <i className="ri-time-line text-2xl text-gray-300 mb-2 block"></i>
                  <p className="text-sm text-gray-400">아직 답변이 등록되지 않았습니다.</p>
                </div>
              ) : null}

              {/* 관리자 답변 작성/수정 폼 */}
              {isAdmin && (showReplyForm || !selectedInquiry.reply_content) && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    maxLength={500}
                    placeholder="답변 내용을 입력해주세요 (최대 500자)"
                    className="w-full bg-white rounded-lg px-4 py-3 text-sm md:text-base h-28 resize-none focus:outline-none focus:ring-2 focus:ring-primary mb-2"
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">{replyText.length}/500</span>
                    <div className="flex gap-2">
                      {showReplyForm && selectedInquiry.reply_content && (
                        <button
                          onClick={() => { setShowReplyForm(false); setReplyText(selectedInquiry.reply_content || ''); }}
                          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 cursor-pointer whitespace-nowrap"
                        >
                          취소
                        </button>
                      )}
                      <button
                        onClick={handleReplySubmit}
                        disabled={replySubmitting || !replyText.trim()}
                        className="bg-primary text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-primary-dark transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        {replySubmitting && <i className="ri-loader-4-line animate-spin text-sm"></i>}
                        {replySubmitting ? '등록 중...' : selectedInquiry.reply_content ? '답변 수정' : '답변 등록'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="bg-red-500 text-white px-5 py-2.5 rounded-lg text-base font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-delete-bin-line"></i>
                삭제
              </button>
              <button
                onClick={closeModals}
                className="bg-primary text-white px-5 py-2.5 rounded-lg text-base font-medium hover:bg-primary-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] px-4" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-white rounded-lg p-6 md:p-8 w-full max-w-[400px] shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-[#222222]">삭제 확인</h3>
              <button onClick={() => setShowDeleteConfirm(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <p className="text-base text-[#222222] mb-6">정말로 이 문의를 삭제하시겠습니까?<br />삭제된 문의는 복구할 수 없습니다.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-5 py-2.5 text-base text-gray-600 hover:text-gray-800 cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={handleDelete}
                className="bg-red-500 text-white px-5 py-2.5 rounded-lg text-base font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {showImagePreview && selectedInquiry?.attachment_url && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70] px-4"
          onClick={() => setShowImagePreview(false)}
        >
          <button
            onClick={() => setShowImagePreview(false)}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/20 rounded-full text-white hover:bg-white/30 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          <img
            src={selectedInquiry.attachment_url}
            alt={selectedInquiry.attachment_name}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
